import { Link, useRouterState } from '@tanstack/react-router';
import {
  AlertTriangle,
  BarChart3,
  Bell,
  BookMarked,
  BookOpen,
  Calendar,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Coins,
  CreditCard,
  FileText,
  KeyRound,
  ListChecks,
  MessageSquare,
  Settings,
  Shield,
  SlidersHorizontal,
  UserCog,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useSidebar } from '../../context/sidebar-context';
import { usePermissions } from '../../hooks/use-permissions';
import { cn } from '../../lib/utils';

interface SubItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  permission?: string | string[];
  authority?: string;
}

interface NavItem {
  label: string;
  icon: React.ReactNode;
  to?: string;
  permission?: string | string[];
  authority?: string;
  subItems?: SubItem[];
}

// A permission array means "any of these" — used where a menu item is shared by
// resources split per municipality (e.g. pledge-config vs sm-pledge-config).
function hasAnyPermission(can: (key: string) => boolean, permission?: string | string[]) {
  if (!permission) return true;
  return Array.isArray(permission) ? permission.some((p) => can(p)) : can(permission);
}

const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    icon: <BarChart3 size={22} />,
    to: '/dashboard',
  },
  {
    label: 'Permit Requests',
    icon: <ClipboardList size={22} />,
    to: '/permits',
    permission: 'permit:view',
    authority: 'KALBA_MUNICIPALITY',
  },
  {
    label: 'Study Requests',
    icon: <BookOpen size={22} />,
    to: '/study-requests',
    permission: 'sm-study:view',
    authority: 'SHARJAH_MUNICIPALITY',
  },
  {
    label: 'My Site Visits',
    icon: <Calendar size={22} />,
    to: '/site-visits',
    permission: 'sm-site-visit:view',
    authority: 'SHARJAH_MUNICIPALITY',
  },
  {
    label: 'All Site Visits',
    icon: <ListChecks size={22} />,
    to: '/all-site-visits',
    permission: 'sm-site-visit:view-all',
    authority: 'SHARJAH_MUNICIPALITY',
  },
  {
    label: 'Study Reports',
    icon: <ClipboardCheck size={22} />,
    to: '/study-reports',
    permission: 'sm-study-report:view',
    authority: 'SHARJAH_MUNICIPALITY',
  },
  {
    label: 'Tahseel Inbound',
    icon: <AlertTriangle size={22} />,
    to: '/tahseel-inbound',
    permission: 'sm-study:tahseel-inbound:view',
    authority: 'SHARJAH_MUNICIPALITY',
  },
  {
    label: 'Customers',
    icon: <Users size={22} />,
    to: '/customers',
    permission: 'customer:view',
  },
  {
    label: 'Inspections',
    icon: <ClipboardCheck size={22} />,
    to: '/inspections',
    permission: 'inspection:view',
    authority: 'KALBA_MUNICIPALITY',
  },
  {
    label: 'Pledge & Study',
    icon: <BookMarked size={22} />,
    to: '/pledge-studies',
    permission: 'pledge:view',
    authority: 'KALBA_MUNICIPALITY',
  },
  {
    label: 'Settings',
    icon: <Settings size={22} />,
    subItems: [
      {
        label: 'Users',
        to: '/users',
        icon: <Users size={18} />,
        permission: 'user:view',
      },
      {
        label: 'Roles',
        to: '/users/roles',
        icon: <Shield size={18} />,
        permission: 'role:view',
      },
      {
        label: 'Request Types',
        to: '/config/request-types',
        icon: <SlidersHorizontal size={18} />,
        permission: 'request-type:view',
      },
      {
        label: 'Document Types',
        to: '/config/document-types',
        icon: <FileText size={18} />,
        permission: 'document-type:view',
      },
      {
        label: 'Fee Components',
        to: '/config/fee-component-types',
        icon: <Coins size={18} />,
        permission: 'fee-component:view',
      },
      {
        label: 'Pledge Studies',
        to: '/config/pledge-study',
        icon: <BookOpen size={18} />,
        permission: ['pledge-config:view', 'sm-pledge-config:view'],
      },
      {
        label: 'Payment Gateway',
        to: '/config/payment-gateway',
        icon: <CreditCard size={18} />,
        permission: 'payment-gateway:view',
        authority: 'KALBA_MUNICIPALITY',
      },
      {
        label: 'API Clients',
        to: '/config/api-clients',
        icon: <KeyRound size={18} />,
        permission: 'api-client:view',
      },
      {
        label: 'User Types',
        to: '/config/user-types',
        icon: <UserCog size={18} />,
        permission: 'user-type:view',
      },
    ],
    permission: 'user:view',
  },
];

const SYSTEM_ITEMS: NavItem[] = [
  {
    label: 'Notifications',
    icon: <Bell size={22} />,
    to: '/notifications',
    permission: 'notification:view',
  },
  {
    label: 'Survey Responses',
    icon: <MessageSquare size={22} />,
    to: '/survey/responses',
    permission: 'survey:view',
  },
];

function NavLink({ item }: { item: NavItem }) {
  const { can, hasAuthority } = usePermissions();
  const { isExpanded, isHovered } = useSidebar();
  const showLabels = isExpanded || isHovered;
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const [open, setOpen] = useState(
    () => item.subItems?.some((s) => currentPath.startsWith(s.to)) ?? false,
  );

  if (item.subItems) {
    const visibleSubs = item.subItems.filter(
      (s) => hasAnyPermission(can, s.permission) && (!s.authority || hasAuthority(s.authority)),
    );
    if (visibleSubs.length === 0) return null;

    // Most-specific match: longest path that prefix-matches wins
    const activeSubTo = visibleSubs.reduce<string | null>((best, s) => {
      const matches = currentPath === s.to || currentPath.startsWith(`${s.to}/`);
      if (!matches) return best;
      if (!best || s.to.length > best.length) return s.to;
      return best;
    }, null);

    const isActive = activeSubTo !== null;
    const isOpen = open || isActive;

    return (
      <li>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'menu-item',
            isActive
              ? 'bg-[var(--color-sidebar-accent)] text-foreground font-semibold'
              : 'menu-item-inactive',
          )}
        >
          <span className="shrink-0 flex items-center">{item.icon}</span>
          {showLabels && (
            <>
              <span className="flex-1 text-left">{item.label}</span>
              <ChevronDown
                size={16}
                className={cn(
                  'shrink-0 transition-transform duration-200 text-gray-400',
                  isOpen && 'rotate-180',
                )}
              />
            </>
          )}
        </button>

        {showLabels && isOpen && (
          <ul className="mt-1 ml-4 pl-4 border-l-2 border-gray-200 space-y-0.5">
            {visibleSubs.map((sub) => {
              const subActive = sub.to === activeSubTo;
              return (
                <li key={sub.to}>
                  <Link
                    to={sub.to}
                    className={cn(
                      'flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-[14px] font-medium transition-all duration-150',
                      subActive
                        ? 'bg-brand-500 text-white shadow-sm'
                        : 'text-gray-500 hover:bg-accent hover:text-foreground',
                    )}
                  >
                    <span className="shrink-0 flex items-center">{sub.icon}</span>
                    <span>{sub.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  }

  const isActive = item.to
    ? currentPath === item.to || currentPath.startsWith(`${item.to}/`)
    : false;

  return (
    <li>
      <Link
        to={item.to!}
        className={cn('menu-item', isActive ? 'menu-item-active' : 'menu-item-inactive')}
      >
        <span className="shrink-0 flex items-center">{item.icon}</span>
        {showLabels && <span>{item.label}</span>}
      </Link>
    </li>
  );
}

export function Sidebar() {
  const { can, hasAuthority, isSharjahOnly } = usePermissions();
  const { isExpanded, isHovered, isMobileOpen, setIsHovered, toggleMobileSidebar } = useSidebar();

  const showLabels = isExpanded || isHovered;
  const sidebarWidth = isExpanded || isHovered ? 'lg:w-64' : 'lg:w-20';

  const visibleNav = NAV_ITEMS.filter(
    (item) =>
      (!item.authority || hasAuthority(item.authority)) &&
      (hasAnyPermission(can, item.permission) ||
        item.subItems?.some(
          (s) => hasAnyPermission(can, s.permission) && (!s.authority || hasAuthority(s.authority)),
        )),
  );

  const visibleSystem = SYSTEM_ITEMS.filter((item) => hasAnyPermission(can, item.permission));

  return (
    <>
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={toggleMobileSidebar} />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 z-[99999] flex h-full flex-col border-r border-gray-200 bg-white transition-all duration-300 ease-in-out',
          isMobileOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full',
          'lg:translate-x-0',
          sidebarWidth,
        )}
        onMouseEnter={() => !isExpanded && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Logo / Header */}
        <div
          className={cn(
            'relative flex items-center justify-center border-b border-gray-200 px-4 shrink-0 transition-all duration-300',
            showLabels ? 'h-36' : 'h-16',
          )}
        >
          <Link
            to="/dashboard"
            className="flex items-center justify-center hover:opacity-90 transition-opacity"
          >
            <img
              src={isSharjahOnly ? '/images/sm/sm.svg' : '/images/Kalba-Logo-Orange.svg'}
              alt={isSharjahOnly ? 'Sharjah Municipality' : 'Kalba Municipality'}
              className={cn(
                'shrink-0 object-contain transition-all duration-300',
                showLabels ? 'w-[150px]' : 'w-10',
              )}
            />
          </Link>
          {showLabels && isMobileOpen && (
            <button
              type="button"
              onClick={toggleMobileSidebar}
              className="absolute right-4 top-4 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 shrink-0 lg:hidden"
            >
              ✕
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-5 space-y-6">
          {/* Primary nav */}
          {visibleNav.length > 0 && (
            <ul className="space-y-1">
              {visibleNav.map((item) => (
                <NavLink key={item.label} item={item} />
              ))}
            </ul>
          )}

          {/* System nav */}
          {visibleSystem.length > 0 && (
            <div>
              {showLabels && (
                <span className="mb-2 block px-4 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  System
                </span>
              )}
              <ul className="space-y-1">
                {visibleSystem.map((item) => (
                  <NavLink key={item.label} item={item} />
                ))}
              </ul>
            </div>
          )}
        </nav>

        {/* Powered by re.life */}
        <div className="flex items-center justify-center gap-1.5 px-3 py-3 border-t border-gray-100">
          {showLabels && <span className="text-[10px] text-gray-400">Powered by</span>}
          <img
            src="/images/ReLife LogoBlack.png"
            alt="re.life"
            className="h-3.5 w-auto object-contain opacity-50 dark:hidden"
          />
          <img
            src="/images/ReLife LogoWhite.png"
            alt="re.life"
            className="h-3.5 w-auto object-contain opacity-60 hidden dark:block"
          />
        </div>
      </aside>
    </>
  );
}
